/**
 * Story-slug helpers — must stay byte-for-byte compatible with the Python
 * port at `backend/pulse-service/pulse_service/slug.py`.
 *
 * Used by the SPA to build `/news/:date/:slug` links when hydrating against
 * pre-v2 data (legacy `samplePulses.ts`) that pre-dates the slug column. DB-
 * backed stories always carry a slug; this is purely a fallback.
 */

import type { PulseStory } from "@altair/shared/types";

const STOPWORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "in",
  "of",
  "on",
  "or",
  "that",
  "the",
  "to",
  "with",
]);

const MAX_SLUG_LEN = 80;

/** URL-safe slug — same rules as `pulse_service.slug.slugify`. */
export function slugify(text: string): string {
  // Accent strip: "café" → "cafe".
  const normalized = text.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");

  // Non-alphanum → space, then lowercase.
  const cleaned = normalized.replace(/[^a-zA-Z0-9]+/g, " ").toLowerCase();

  const words = cleaned.split(/\s+/).filter((w) => w && !STOPWORDS.has(w));
  let slug = words.join("-");

  if (slug.length > MAX_SLUG_LEN) {
    const truncated = slug.slice(0, MAX_SLUG_LEN);
    const lastHyphen = truncated.lastIndexOf("-");
    slug = lastHyphen > 0 ? truncated.slice(0, lastHyphen) : truncated;
  }

  return slug.replace(/^-+|-+$/g, "");
}

/**
 * Return the story's persisted slug when available, else synthesize one from
 * its headline. Returns `""` for the pathological case where the headline is
 * empty or only stopwords — callers should treat that as "no deep link".
 */
export function resolveStorySlug(story: PulseStory): string {
  if (story.slug && story.slug.length > 0) return story.slug;
  return slugify(story.headline);
}
